import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PaperPlaneRight, Sparkle, X, Rocket, TrendUp, Bank, ChartLineUp, Globe, Handshake, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const ICON_MAP = {
  launch_business: Rocket,
  evaluate_investment: TrendUp,
  create_holding: Bank,
  raise_capital: ChartLineUp,
  expand_internationally: Globe,
  structure_deal: Handshake,
};

const WORKFLOW_LABEL_LOOKUP = {
  "Launch a Business": "launch_business",
  "Evaluate an Investment": "evaluate_investment",
  "Create a Holding": "create_holding",
  "Raise Capital": "raise_capital",
  "Expand Internationally": "expand_internationally",
  "Structure a Deal": "structure_deal",
};

/**
 * Orixa AI Analyst 2.0 panel — workspace-aware streaming chat.
 *
 * Props:
 *   workspaceId  : optional string — scopes the analyst to a workspace
 *   onClose      : optional callback to close the panel (modal mode)
 *   variant      : 'docked' (default) or 'inline'
 */
export default function AIAnalystPanel({ workspaceId, onClose, variant = "docked" }) {
  const [messages, setMessages] = useState([
    {
      role: "analyst",
      content: workspaceId
        ? "I have full context on this workspace. Ask me anything — comparisons, risk drill-downs, structuring advice, market entry sequencing."
        : "Ask me anything about Emerging Markets investment, structuring or origination. I'll always finish with a Next Recommended Step.",
    },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || streaming) return;
    setMessages((m) => [...m, { role: "user", content: trimmed }, { role: "analyst", content: "" }]);
    setInput("");
    setStreaming(true);

    try {
      const token = localStorage.getItem("orixa_token") || "";
      const API = process.env.REACT_APP_BACKEND_URL + "/api";
      const resp = await fetch(`${API}/analyst/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ workspace_id: workspaceId || null, message: trimmed }),
      });
      if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "analyst", content: acc };
          return copy;
        });
      }
    } catch (err) {
      toast.error("Analyst error");
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "analyst", content: "_Error reaching the analyst — please retry._" };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const containerClass = variant === "inline"
    ? "border border-border bg-card rounded-2xl shadow-xl flex flex-col h-[640px] max-h-[80vh]"
    : "fixed bottom-4 right-4 w-[min(440px,calc(100vw-2rem))] h-[min(680px,calc(100vh-6rem))] border border-border bg-card rounded-2xl shadow-2xl flex flex-col z-50";

  return (
    <div className={containerClass} data-testid="ai-analyst-panel">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border flex items-center gap-2.5 flex-shrink-0">
        <div className="h-8 w-8 bg-teal-soft text-teal rounded-md grid place-items-center">
          <Sparkle size={16} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-sm tracking-tight">Orixa AI Analyst</div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
            {workspaceId ? "Workspace-aware" : "Global · Emerging Markets"}
          </div>
        </div>
        {onClose && (
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1" data-testid="analyst-close">
            <X size={16} />
          </button>
        )}
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="analyst-messages">
        {messages.map((m, i) => (
          <Message
            key={`amsg-${i}-${m.role}`}
            role={m.role}
            content={m.content}
            isLast={i === messages.length - 1}
            streaming={streaming}
            onWorkflowClick={(wfId) => navigate(`/workflows/${wfId}${workspaceId ? `?workspace=${workspaceId}` : ""}`)}
          />
        ))}
      </div>

      {/* Input */}
      <form onSubmit={send} className="border-t border-border p-3 flex items-center gap-2 flex-shrink-0">
        <input
          type="text" value={input} onChange={(e) => setInput(e.target.value)}
          placeholder="Ask the analyst…"
          disabled={streaming}
          className="flex-1 bg-background border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          data-testid="analyst-input"
        />
        <button type="submit" disabled={streaming || !input.trim()}
          className="bg-primary text-primary-foreground rounded-md w-9 h-9 grid place-items-center hover:bg-primary/90 disabled:opacity-50"
          data-testid="analyst-send"
        >
          <PaperPlaneRight size={14} weight="bold" />
        </button>
      </form>
    </div>
  );
}


function Message({ role, content, isLast, streaming, onWorkflowClick }) {
  const isUser = role === "user";
  const detected = detectNextStep(content || "");

  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[88%] bg-primary text-primary-foreground px-3.5 py-2 rounded-2xl rounded-br-sm text-sm leading-relaxed whitespace-pre-wrap">
          {content}
        </div>
      </div>
    );
  }

  if (!content && isLast && streaming) {
    return <div className="text-muted-foreground italic text-xs">thinking…</div>;
  }

  return (
    <div className="text-sm">
      <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1.5">Analyst</div>
      <div className="leading-relaxed whitespace-pre-wrap text-foreground prose-analyst">
        {renderMarkdown(content || "")}
      </div>
      {detected && detected.workflowId && (
        <button
          type="button"
          onClick={() => onWorkflowClick(detected.workflowId)}
          className="mt-3 inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-3 py-2 text-xs font-semibold hover:bg-primary/90 card-elev"
          data-testid="analyst-cta-next-step"
        >
          {(() => { const Icon = ICON_MAP[detected.workflowId] || Sparkle; return <Icon size={14} weight="duotone" />; })()}
          {detected.label} <ArrowRight size={12} weight="bold" />
        </button>
      )}
    </div>
  );
}


function detectNextStep(text) {
  // Strip markdown bold/italic markers before regex matching
  const clean = (text || "").replace(/\*\*/g, "").replace(/[_*]/g, "");
  const m = clean.match(/Proceed to ([A-Za-z &]+?)(?:\s*[—\-:•\n]|$)/);
  if (!m) return null;
  const candidate = m[1].trim();
  for (const [label, id] of Object.entries(WORKFLOW_LABEL_LOOKUP)) {
    if (candidate.toLowerCase().startsWith(label.toLowerCase().slice(0, 8))) {
      return { workflowId: id, label: `Proceed to ${label}` };
    }
  }
  return null;
}


function renderMarkdown(text) {
  // Minimal **bold** + line-break handling — no full markdown parser to keep deps light.
  const lines = text.split("\n");
  return lines.map((line, i) => {
    const parts = [];
    let lastIndex = 0;
    const re = /\*\*([^*]+)\*\*/g;
    let match;
    while ((match = re.exec(line)) !== null) {
      if (match.index > lastIndex) parts.push(line.slice(lastIndex, match.index));
      parts.push(<strong key={`b-${i}-${match.index}`}>{match[1]}</strong>);
      lastIndex = re.lastIndex;
    }
    if (lastIndex < line.length) parts.push(line.slice(lastIndex));
    return (
      <span key={`ln-${i}`}>
        {parts.length ? parts : line}
        {i < lines.length - 1 ? <br /> : null}
      </span>
    );
  });
}
